import React, { useState, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isToday, parseISO, setHours, setMinutes } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateForDisplay, parseUserInput, convertToUTC } from '../../utils/datetime';
import { useTimezone } from '../../context/TimezoneContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';

interface DateTimePickerProps {
  value: string;
  onChange: (value: string | null) => void;
  onClose?: () => void;
  allowClear?: boolean;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({ value, onChange, allowClear = true }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { timezone } = useTimezone();

  const isInitialValueEmpty = (val: string) => val === null || val === '';

  const [currentDate, setCurrentDate] = useState(() => {
    return !isInitialValueEmpty(value) ? parseISO(value) : new Date();
  });

  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    return !isInitialValueEmpty(value) ? parseISO(value) : null;
  });

  const [error, setError] = useState<string | null>(null);
  const [dateInput, setDateInput] = useState(() => {
    return !isInitialValueEmpty(value) ? formatDateForDisplay(value, timezone) : '';
  });
  
  useEffect(() => {
    if (!isInitialValueEmpty(value)) {
      setDateInput(formatDateForDisplay(value, timezone));
      setSelectedDate(parseISO(value));
      setIsValid(true);
      setError(null);
    } else {
      if (allowClear) {
        setDateInput('');
        setSelectedDate(null);
        setIsValid(true);
        setError(null);
      } else {
        const now = new Date();
        setDateInput(formatDateForDisplay(convertToUTC(now, timezone), timezone));
        setSelectedDate(now);
        setIsValid(false);
        setError('Este campo es obligatorio.');
      }
    }
  }, [value, timezone, allowClear]);

  const [isValid, setIsValid] = useState(true);
  const [open, setOpen] = useState(false);

  const timeSlots = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = (i % 2) * 30;
    return format(setMinutes(setHours(new Date(), hour), minute), 'HH:mm');
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDateInput(inputValue);

    if (inputValue === '') {
      if (allowClear) {
        setSelectedDate(null);
        setIsValid(true);
        setError(null);
      } else {
        setSelectedDate(null);
        setIsValid(false);
        setError('Este campo es obligatorio.');
      }
    } else {
      const { date, error: parseError } = parseUserInput(inputValue, timezone);
      setSelectedDate(date || selectedDate);
      setIsValid(!!date);
      setError(parseError);
      if (!date && !allowClear) {
        setError(parseError || 'Formato de fecha inválido.');
      } else if (!date && allowClear && inputValue !== '') {
        setError(parseError || 'Formato de fecha inválido.');
      }
    }
  };

  const handleDateSelect = (date: Date) => {
    setError(null);
    const newDate = setMinutes(
      setHours(date, selectedDate ? selectedDate.getHours() : 0),
      selectedDate ? selectedDate.getMinutes() : 0
    );
    
    setSelectedDate(newDate);
    setDateInput(formatDateForDisplay(convertToUTC(newDate, timezone), timezone));
    setIsValid(true);
  };

  const handleTimeSelect = (hour: number, minute: number) => {
    const baseDate = selectedDate || new Date();
    const newDate = setMinutes(setHours(baseDate, hour), minute);
    setSelectedDate(newDate);
    setDateInput(formatDateForDisplay(convertToUTC(newDate, timezone), timezone));
    setIsValid(true);
  };

  const handleSave = () => {
    if (dateInput === '' && selectedDate === null) {
      if (allowClear) {
        onChange(null);
        setOpen(false);
      } else {
        setError('Este campo es obligatorio.');
        setIsValid(false);
      }
    } else if (isValid && selectedDate) {
      onChange(convertToUTC(selectedDate, timezone));
      setOpen(false);
      setError(null);
    } else if (!isValid) {
      if (!error && dateInput === '') {
        setError('Este campo es obligatorio.');
      } else if (!error) {
        setError('Formato de fecha inválido.');
      }
    }
  };

  const handleClear = () => {
    if (allowClear) {
      setDateInput('');
      setSelectedDate(null);
      setIsValid(true);
      setError(null);
      onChange(null);
      setOpen(false);
    }
  };
  
  // Generate calendar days with proper week alignment
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfMonth(currentDate);
    
    // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
    const startingDayOfWeek = startDate.getDay();
    
    // Convert to Monday-first format (0 = Monday, 6 = Sunday)
    const mondayFirstStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < mondayFirstStartDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
    days.push(...monthDays);
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  const DateView = () => (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrentDate(subMonths(currentDate, 1))}
          className="p-1 hover:bg-gray-100"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h2 className="text-sm font-medium">
          {format(currentDate, 'MMMM yyyy', { locale: language === 'es' ? es : enUS })}
        </h2>
        <button
          onClick={() => setCurrentDate(addMonths(currentDate, 1))}
          className="p-1 hover:bg-gray-100"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {(t('datePicker.dayInitials', { returnObjects: true }) as string[]).map((day, index) => (
          <div key={`${day}-${index}`} className="text-xs font-medium text-gray-500 text-center py-1">
            {day}
          </div>
        ))}
        {calendarDays.map((day, i) => (
          <div key={i} className="flex items-center justify-center">
            {day ? (
              <button
                onClick={() => handleDateSelect(day)}
                className={`
                  w-8 h-8 text-sm flex items-center justify-center
                  ${!isSameMonth(day, currentDate) ? 'text-gray-300' : ''}
                  ${selectedDate && isSameDay(day, selectedDate) ? 'bg-[#0055FF] text-white' : ''}
                  ${isToday(day) ? 'font-bold' : ''}
                  ${(!selectedDate || !isSameDay(day, selectedDate)) ? 'hover:bg-gray-100' : ''}
                `}
              >
                {format(day, 'd')}
              </button>
            ) : (
              <div className="w-8 h-8"></div>
            )}
          </div>
        ))}
      </div>
      
      {error && (
        <p className="text-xs text-red-500 mt-2">{error}</p>
      )}
    </div>
  );

  const TimeList = () => (
    <div className="p-4 border-t border-gray-200">
      <h3 className="text-sm font-medium text-gray-700 mb-2">{t('datePicker.time')}</h3>
      <div className="overflow-y-auto max-h-48">
        <div className="space-y-1">
          {timeSlots.map((time) => (
            <button
              key={time}
              onClick={() => {
                const [hours, minutes] = time.split(':').map(Number);
                handleTimeSelect(hours, minutes);
              }}
              className={`
                w-full px-3 py-1.5 text-sm text-left
                ${selectedDate && format(selectedDate, 'HH:mm') === time
                  ? 'bg-[#0055FF] text-white'
                  : 'hover:bg-gray-100 text-gray-700'
                }
              `}
            >
              {time}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button 
          className="text-sm group" 
          onClick={(e) => e.stopPropagation()}
        >
          {value ? (
            <span className="text-gray-600 hover:text-gray-900">{dateInput}</span>
          ) : (
            <span className="text-gray-300 group-hover:text-gray-500">
              {t('datePicker.selectDate')}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          onInteractOutside={() => setOpen(false)}
          className="bg-white shadow-lg border border-gray-200 w-64 z-[1000]"
          sideOffset={5}
        >
          <div className="p-4 border-b border-gray-200 relative">
            <input
              type="text"
              value={dateInput}
              onChange={handleInputChange}
              placeholder="dd/MM/yyyy HH:mm o vacío"
              className={`w-full px-3 py-2 text-sm border focus:outline-none focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF] ${
                !isValid && dateInput !== '' ? 'border-red-500' : 'border-gray-300'
              } pr-8`}
            />
            {allowClear && dateInput && (
              <button
                onClick={handleClear}
                className="absolute right-6 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                aria-label="Clear date"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            )}
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <DateView />
          <TimeList />
          <div className={`flex items-center ${allowClear ? 'justify-between' : 'justify-end'} p-4 border-t border-gray-200`}>
            {allowClear && (
              <button 
                onClick={handleClear} 
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Limpiar
              </button>
            )}
            <button onClick={handleSave}
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors ${!allowClear ? 'w-full' : ''}`}>Guardar</button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};